// SPDX-License-Identifier: AGPL-3.0-or-later

import {Routes} from '@app/app/Routes';
import {ExternalLink} from '@app/features/app/components/shared/ExternalLink';
import authStyles from '@app/features/auth/flow/AuthPageStyles.module.css';
import dobStyles from '@app/features/auth/flow/DateOfBirthField.module.css';
import {PASSWORD_MANAGER_IGNORE_ATTRIBUTES} from '@app/features/platform/utils/PasswordManagerAutocomplete';
import {Button} from '@app/features/ui/button/Button';
import {Checkbox} from '@app/features/ui/checkbox/Checkbox';
import inputStyles from '@app/features/ui/components/form/Input.module.css';
import {getCurrentLocale} from '@app/features/user/utils/LocaleUtils';
import {getDateFieldOrder} from '@app/vendor/date_utils/DateIntrospection';
import {Trans, useLingui} from '@lingui/react/macro';
import {useMemo} from 'react';

type DateFieldType = 'month' | 'day' | 'year';
interface MockMinimalRegisterFormProps {
	submitLabel: React.ReactNode;
}
export function MockMinimalRegisterForm({submitLabel}: MockMinimalRegisterFormProps) {
	const {t} = useLingui();
	const locale = getCurrentLocale();
	const fieldOrder = useMemo(() => getDateFieldOrder(locale), [locale]);
	const dateFields: Record<DateFieldType, React.ReactElement> = {
		month: (
			<div key="month" className={dobStyles.monthField}>
				<input
					type="text"
					{...PASSWORD_MANAGER_IGNORE_ATTRIBUTES}
					readOnly
					tabIndex={-1}
					placeholder={t`Month`}
					className={inputStyles.input}
				/>
			</div>
		),
		day: (
			<div key="day" className={dobStyles.dayField}>
				<input
					type="text"
					{...PASSWORD_MANAGER_IGNORE_ATTRIBUTES}
					readOnly
					tabIndex={-1}
					placeholder={t`Day`}
					className={inputStyles.input}
				/>
			</div>
		),
		year: (
			<div key="year" className={dobStyles.yearField}>
				<input
					type="text"
					{...PASSWORD_MANAGER_IGNORE_ATTRIBUTES}
					readOnly
					tabIndex={-1}
					placeholder={t`Year`}
					className={inputStyles.input}
				/>
			</div>
		),
	};
	const orderedFields = fieldOrder.map((fieldType) => dateFields[fieldType]);
	return (
		<div className={authStyles.form}>
			<div className={inputStyles.fieldset}>
				<div className={inputStyles.labelContainer}>
					<span className={inputStyles.label}>
						<Trans>Display Name (Optional)</Trans>
					</span>
				</div>
				<div className={inputStyles.inputGroup}>
					<input
						type="text"
						{...PASSWORD_MANAGER_IGNORE_ATTRIBUTES}
						readOnly
						tabIndex={-1}
						placeholder={t`What should people call you?`}
						className={inputStyles.input}
					/>
				</div>
			</div>
			<div className={dobStyles.fieldset}>
				<div className={dobStyles.labelContainer}>
					<span className={dobStyles.legend}>
						<Trans>Date of Birth</Trans>
					</span>
				</div>
				<div className={dobStyles.inputsContainer}>
					<div className={dobStyles.fieldsRow}>{orderedFields}</div>
				</div>
			</div>
			<div className={authStyles.consentRow}>
				<Checkbox checked={false} onChange={() => {}} disabled>
					<span className={authStyles.consentLabel}>
						<Trans>
							I agree to the{' '}
							<ExternalLink href={Routes.terms()} className={authStyles.policyLink}>
								Terms of Service
							</ExternalLink>{' '}
							and{' '}
							<ExternalLink href={Routes.privacy()} className={authStyles.policyLink}>
								Privacy Policy
							</ExternalLink>
						</Trans>
					</span>
				</Checkbox>
			</div>
			<Button type="button" fitContainer disabled>
				{submitLabel}
			</Button>
		</div>
	);
}
